import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import { unsubscribeFromMeetingEmails } from "../../lib/api.js";
import { Button, ScreenHeading, StatusLine } from "../ui";
import { Screen } from "./screen";
import { useScreenStatus } from "./use-screen-status";

/** What the page is doing or did; the registry's three states and the busy one. */
export type UnsubscribeStage = "ask" | "working" | "confirmed" | "bad_link";

/**
 * The meeting email's unsubscribe page (#864).
 *
 * A person lands here by following the link in a "Ready" or "Notes not ready" email:
 * the API's one-click endpoint redirects mailed links here with the token in the query.
 * It asks the one question the mailbox provider's own one-click Unsubscribe button
 * skips -- nothing turns off until the tap, because some mail scanners run a page's
 * script, exactly why the delete page asks once more too (#781).
 *
 * Nobody is signed in here and the endpoint needs no sign-in: the token is the whole
 * grant, and it can only turn this account's meeting emails off. Turning them back on
 * is the Settings toggle's job, in the app or on the web, which both closing states name.
 */
export function Unsubscribe() {
  const [params] = useSearchParams();
  const [stage, setStage] = useState<UnsubscribeStage>("ask");
  const [status, say] = useScreenStatus();
  // The token is spent from memory; leaving it in the URL would put it in history.
  const [token, setToken] = useState(() => params.get("token") ?? "");

  useEffect(() => {
    setToken((value) => {
      if (value && window.location.search.includes("token=")) {
        window.history.replaceState(null, "", window.location.pathname);
      }
      return value;
    });
    // Read once: the address bar holds the only copy before it moves into state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function confirm() {
    setStage("working");
    try {
      await unsubscribeFromMeetingEmails(token);
      captureAnalytics("email_meetings_unsubscribed", {});
      say("");
      setStage("confirmed");
    } catch (err) {
      if ((err as { status?: number }).status === 404) {
        setStage("bad_link");
      } else {
        setStage("ask");
        say(t("common.try_again"), "err");
      }
    }
  }

  return (
    <Screen name="email_unsubscribe">
      {stage === "confirmed" || stage === "bad_link" ? (
        <>
          <ScreenHeading>{t("email_unsubscribe.heading")}</ScreenHeading>
          <p className="m-0 text-lede leading-normal" role="status">
            {t(stage === "confirmed" ? "email_unsubscribe.confirmed" : "email_unsubscribe.bad_link")}
          </p>
        </>
      ) : (
        <>
          <ScreenHeading>{t("email_unsubscribe.heading")}</ScreenHeading>
          <p className="m-0 text-lede leading-normal">{t("email_unsubscribe.lede")}</p>
          <Button
            className="w-full"
            disabled={stage === "working" || token.trim() === ""}
            onClick={() => void confirm()}
          >
            {stage === "working" ? t("email_unsubscribe.confirming") : t("email_unsubscribe.confirm")}
          </Button>
        </>
      )}
      <StatusLine kind={status.kind}>{status.message}</StatusLine>
    </Screen>
  );
}